import { format } from "date-fns";
import type { ReviewOverviewSnapshotHighlight } from "@/services/bedrock";
import type { XlsxColumnDef, XlsxColumnType, XlsxTableSection } from "@/utils/xlsxExportHelpers";
import { CATEGORY_CONFIG, CATEGORY_ORDER } from "./PeriodInsights";

export type ExportCountRow = {
  metric: string;
  period: number;
  previous: number;
  allTime: number;
  coachPeriod: number;
  coachAllTime: number;
  share?: number | null;
};

type AiInsightsExportInput = {
  summary?: string | null;
  highlights?: ReviewOverviewSnapshotHighlight[] | null;
  generatedAt?: string | null;
  showTeamHighlights: boolean;
};

type ExportHeroValue = {
  metric: string;
  periodValue: number;
  periodType: XlsxColumnType;
  previousDelta?: number | null;
  deltaType?: XlsxColumnType;
  allTimeValue: number;
  allTimeType: XlsxColumnType;
};

function delta(current: number, previous: number | null | undefined, hasPrevious: boolean): number | null {
  if (!hasPrevious || previous == null) return null;
  return current - previous;
}

function buildMetricExportSection({
  title,
  hero,
  rows,
  isAllTimePeriod,
  showCoachShare,
  hasPrevious,
  countDeltaType = "delta",
}: {
  title: string;
  hero: ExportHeroValue;
  rows: ExportCountRow[];
  isAllTimePeriod: boolean;
  showCoachShare: boolean;
  hasPrevious: boolean;
  countDeltaType?: XlsxColumnType;
}): XlsxTableSection {
  const periodHeader = isAllTimePeriod ? "All time" : "This period";
  const columns: XlsxColumnDef[] = [
    { key: "Metric", header: "Metric", type: "string", wch: 22 },
    { key: "period", header: periodHeader, type: hero.periodType },
  ];
  if (!isAllTimePeriod) {
    columns.push({ key: "delta", header: "vs prior", type: hero.deltaType ?? countDeltaType });
    columns.push({ key: "allTime", header: "All time", type: hero.allTimeType });
  }
  if (showCoachShare) {
    if (isAllTimePeriod) {
      columns.push({ key: "coachPeriod", header: "Company", type: "integer" });
    } else {
      columns.push({ key: "coachPeriod", header: "Company (period)", type: "integer" });
      columns.push({ key: "coachAllTime", header: "Company (all time)", type: "integer" });
    }
  }
  const hasShare = rows.some(row => row.share != null);
  if (hasShare) {
    columns.push({ key: "share", header: "Share", type: "percent" });
  }

  const sectionRows: XlsxTableSection["rows"] = [
    {
      Metric: hero.metric,
      period: { value: hero.periodValue, type: hero.periodType },
      delta:
        !isAllTimePeriod && hero.previousDelta != null
          ? { value: hero.previousDelta, type: hero.deltaType ?? countDeltaType }
          : "",
      allTime: { value: hero.allTimeValue, type: hero.allTimeType },
      coachPeriod: "",
      coachAllTime: "",
      share: "",
    },
    ...rows.map(row => {
      const rowDelta = !isAllTimePeriod && hasPrevious ? delta(row.period, row.previous, hasPrevious) : null;
      return {
        Metric: row.metric,
        period: { value: row.period, type: "integer" as const },
        delta: rowDelta != null ? { value: rowDelta, type: countDeltaType } : "",
        allTime: { value: row.allTime, type: "integer" as const },
        coachPeriod: { value: row.coachPeriod, type: "integer" as const },
        coachAllTime: { value: row.coachAllTime, type: "integer" as const },
        share: row.share != null ? { value: row.share, type: "percent" as const } : "",
      };
    }),
  ];

  return { title, columns, rows: sectionRows, emphasizeFirstRow: true };
}

/**
 * AI summary + highlights mirrored into the export.
 * Detail spans the remaining metric-card columns so it does not widen them.
 */
function buildAiInsightsExportSection(
  { summary, highlights, generatedAt, showTeamHighlights }: AiInsightsExportInput,
  metricColumnCount: number
): XlsxTableSection | null {
  const trimmedSummary = summary?.trim();
  const visibleHighlights = (highlights ?? []).filter(
    highlight =>
      highlight.text?.trim() &&
      CATEGORY_ORDER.includes(highlight.category) &&
      (highlight.category !== "team" || showTeamHighlights)
  );

  if (!trimmedSummary && visibleHighlights.length === 0) return null;

  const rows: XlsxTableSection["rows"] = [];
  if (trimmedSummary) rows.push({ Insight: "Summary", Detail: trimmedSummary });

  CATEGORY_ORDER.forEach(category => {
    visibleHighlights
      .filter(highlight => highlight.category === category)
      .forEach(highlight => {
        rows.push({ Insight: CATEGORY_CONFIG[category].label, Detail: highlight.text.trim() });
      });
  });

  if (generatedAt) {
    rows.push({ Insight: "AI generated at", Detail: format(new Date(generatedAt), "yyyy-MM-dd HH:mm") });
  }

  return {
    title: "AI insights",
    columns: [
      { key: "Insight", header: "Insight", type: "string", wch: 24 },
      { key: "Detail", header: "Detail", type: "string", span: Math.max(1, metricColumnCount - 1) },
    ],
    rows,
  };
}

export type PeriodHealthExportSectionsInput = {
  isAllTimePeriod: boolean;
  showCoachShare: boolean;
  showTeamHighlights: boolean;
  hasPrevious: boolean;
  hasPreviousReplies: boolean;
  periodTotal: number;
  previousTotal: number;
  allTimeTotal: number;
  currentHandledRate: number;
  previousHandledRate: number | null;
  allTimeHandledRate: number;
  currentPositive: number;
  previousPositive: number | null;
  allTimePositive: number;
  avgStar: number;
  previousAvgStar: number;
  allTimeAvgStar: number;
  npsNet: number;
  previousNpsNet: number;
  allTimeNpsNet: number;
  avgTenPoint: number;
  previousAvgTenPoint: number;
  allTimeAvgTenPoint: number;
  reviewSeries: ExportCountRow[];
  replySeries: ExportCountRow[];
  sentimentSeries: ExportCountRow[];
  starSeries: ExportCountRow[];
  npsSeries: ExportCountRow[];
  tenPointSeries: ExportCountRow[];
  aiSummary?: string | null;
  aiHighlights?: ReviewOverviewSnapshotHighlight[] | null;
  aiGeneratedAt?: string | null;
};

export function buildPeriodHealthExportSections(input: PeriodHealthExportSectionsInput): XlsxTableSection[] {
  const {
    isAllTimePeriod,
    showCoachShare,
    showTeamHighlights,
    hasPrevious,
    hasPreviousReplies,
    periodTotal,
    previousTotal,
    allTimeTotal,
    currentHandledRate,
    previousHandledRate,
    allTimeHandledRate,
    currentPositive,
    previousPositive,
    allTimePositive,
    avgStar,
    previousAvgStar,
    allTimeAvgStar,
    npsNet,
    previousNpsNet,
    allTimeNpsNet,
    avgTenPoint,
    previousAvgTenPoint,
    allTimeAvgTenPoint,
    reviewSeries,
    replySeries,
    sentimentSeries,
    starSeries,
    npsSeries,
    tenPointSeries,
    aiSummary,
    aiHighlights,
    aiGeneratedAt,
  } = input;

  const sectionOpts = {
    isAllTimePeriod,
    showCoachShare,
    hasPrevious,
  };

  const metricSections = [
    buildMetricExportSection({
      title: "Reviews",
      hero: {
        metric: "Total reviews",
        periodValue: periodTotal,
        periodType: "integer",
        previousDelta: delta(periodTotal, previousTotal, hasPrevious),
        deltaType: "delta",
        allTimeValue: allTimeTotal,
        allTimeType: "integer",
      },
      rows: reviewSeries,
      ...sectionOpts,
    }),
    buildMetricExportSection({
      title: "Replies",
      hero: {
        metric: "Handled (replied or no reply needed)",
        periodValue: currentHandledRate / 100,
        periodType: "percent",
        previousDelta: previousHandledRate != null ? delta(currentHandledRate, previousHandledRate, hasPrevious) : null,
        deltaType: "deltaPercentPoints",
        allTimeValue: allTimeHandledRate / 100,
        allTimeType: "percent",
      },
      rows: replySeries,
      ...sectionOpts,
      hasPrevious: hasPreviousReplies,
    }),
    buildMetricExportSection({
      title: "Sentiment",
      hero: {
        metric: "Positive sentiment rate",
        periodValue: currentPositive / 100,
        periodType: "percent",
        previousDelta: previousPositive != null ? delta(currentPositive, previousPositive, hasPrevious) : null,
        deltaType: "deltaPercentPoints",
        allTimeValue: allTimePositive / 100,
        allTimeType: "percent",
      },
      rows: sentimentSeries,
      ...sectionOpts,
    }),
    buildMetricExportSection({
      title: "Star ratings",
      hero: {
        metric: "Average rating",
        periodValue: avgStar,
        periodType: "decimal",
        previousDelta: delta(avgStar, previousAvgStar, hasPrevious),
        deltaType: "deltaDecimal",
        allTimeValue: allTimeAvgStar,
        allTimeType: "decimal",
      },
      rows: starSeries,
      ...sectionOpts,
    }),
    buildMetricExportSection({
      title: "NPS",
      hero: {
        metric: "Net score",
        periodValue: npsNet,
        periodType: "integer",
        previousDelta: delta(npsNet, previousNpsNet, hasPrevious),
        deltaType: "delta",
        allTimeValue: allTimeNpsNet,
        allTimeType: "integer",
      },
      rows: npsSeries,
      ...sectionOpts,
    }),
    buildMetricExportSection({
      title: "10-point ratings",
      hero: {
        metric: "Average rating",
        periodValue: avgTenPoint,
        periodType: "decimal",
        previousDelta: delta(avgTenPoint, previousAvgTenPoint, hasPrevious),
        deltaType: "deltaDecimal",
        allTimeValue: allTimeAvgTenPoint,
        allTimeType: "decimal",
      },
      rows: tenPointSeries,
      ...sectionOpts,
    }),
  ];

  const metricColumnCount = Math.max(...metricSections.map(section => section.columns.length));
  const aiSection = buildAiInsightsExportSection(
    {
      summary: aiSummary,
      highlights: aiHighlights,
      generatedAt: aiGeneratedAt,
      showTeamHighlights,
    },
    metricColumnCount
  );

  return [...(aiSection ? [aiSection] : []), ...metricSections];
}
